import { useCurrentUser } from "@/hooks/useAuth";
import { useFavoriteIds, useToggleFavorite } from "@/hooks/useUserData";

interface Props {
  postId: number;
  className?: string;
  /** ``compact`` shows just the heart; default also shows ★ Favorited / ☆ Favorite text. */
  compact?: boolean;
}

/** Heart toggle. Hidden entirely for anonymous users since they can't have favs.
 *  Optimistic updates make the flip feel instant; on error the heart snaps back. */
export function FavoriteButton({ postId, className = "", compact = false }: Props) {
  const { data: user } = useCurrentUser();
  const { data: ids } = useFavoriteIds();
  const { mutate, isPending } = useToggleFavorite();

  if (!user) return null;

  const favorited = ids?.has(postId) ?? false;

  function onClick(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    mutate({ postId, favorite: !favorited });
  }

  const colorClass = favorited
    ? "text-red-400 hover:text-red-300"
    : "text-zinc-400 hover:text-red-400";

  if (compact) {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={isPending}
        aria-pressed={favorited}
        aria-label={favorited ? "Remove from favorites" : "Add to favorites"}
        className={`transition-colors ${colorClass} ${className}`}
      >
        {favorited ? "♥" : "♡"}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isPending}
      aria-pressed={favorited}
      className={`inline-flex items-center gap-1.5 text-sm transition-colors ${colorClass} ${className}`}
    >
      <span className="text-base">{favorited ? "♥" : "♡"}</span>
      <span>{favorited ? "Favorited" : "Favorite"}</span>
    </button>
  );
}
